import type { Dictionary } from './dictionaryTypes';

export const EN_ADMIN_WIREGUARD_PAGE = {
    shellTitle: 'Internal WireGuard',
    shellDescription:
        'Inspect internal control-plane WireGuard peers from the outside host, see recent handshakes, and disable compromised operator peers without leaving the admin surface.',
    disablePeerDialogTitle: 'Disable peer',
    summaryPeersLabel: 'Peers',
    summaryMetaLine: '{active} active · {inactive} stale · {never} never connected',
    summaryInterfaceLabel: 'Interface',
    summaryGeneratedLine: 'Generated {time}',
    transportPolicyLabel: 'Transport boundary',
    transportPolicyWireguardCopy: 'This page controls legacy WireGuard peers for the intranet/control-plane path only.',
    transportPolicyAmneziaCopy:
        'User-facing regular VPN AmneziaWG hosts and VPN+ VLESS/H1Cloud are separate surfaces until backend/dev-tools expose transport-aware live controls.',
    transportPolicyMediaWorkerCopy: 'Media workers for /transcribe, /describe, and Suno stay outside this page.',
    snapshotEyebrow: 'Live snapshot',
    snapshotPollCopy: 'Polls every {seconds} seconds while visible. Disable uses the outside host directly.',
    peerCountBadge: '{count} peers',
    badgeNoData: 'No data',
    btnRefresh: 'Refresh',
    btnRefreshBusy: 'Refreshing…',
    errLoadPeers: 'Could not load WireGuard peers.',
    tableColPeer: 'Peer',
    tableColStatus: 'Status',
    tableColAllowedIps: 'Allowed IPs',
    tableColHandshake: 'Handshake',
    tableColTraffic: 'Traffic',
    tableColEndpoint: 'Endpoint',
    tableColAction: 'Action',
    peerUnlabeled: 'Unlabeled peer',
    peerNoIdComment: 'No peer id comment',
    trafficRxPrefix: 'RX',
    trafficTxPrefix: 'TX',
    btnDisable: 'Disable',
    emptyTitle: 'No peers',
    emptyCopy: 'WireGuard returned no configured peers for this interface, or the outside host is not reachable.',
    sheetDisableWarning: 'This removes the peer from the outside WireGuard interface immediately.',
    sheetPeerLabel: 'Peer',
    sheetStatusLabel: 'Status',
    btnCancel: 'Cancel',
    btnDisablePeer: 'Disable peer',
    btnDisablePeerBusy: 'Disabling…',
    disablePeerSuccessLine: 'Disabled peer {display}.',
    errDisablePeer: 'Could not disable peer.',
    valueEmDash: '—',
} satisfies Dictionary['admin']['wireguardPage'];

export const EN_ADMIN_INFRASTRUCTURE = {
    shellTitle: 'Infrastructure',
    shellEyebrow: 'Admin',
    shellDescription: 'Rollout controls, host health, and cross-host runtime truth for the current shared Chattenger fleet.',
    surfaceTabsLabel: 'Infrastructure surface',
    tabRollout: 'Rollout',
    tabHosts: 'Hosts',
    rolloutEyebrow: 'Rollout',
    rolloutTitle: 'Next.js rollout and branch operations',
    rolloutCopy:
        'Use this page for Next.js deploys and branch workflow: pushed Git refs only, stable per-environment deploy roots, and explicit remote dependencies.',
    operatorBoundaryEyebrow: 'Runtime boundary',
    operatorBoundaryTitle: 'VPN and media responsibilities',
    operatorBoundaryCopy:
        'This page reports runtime dependencies. It does not create VPN peers, bootstrap AmneziaWG hosts, shape quotas, or move media workloads.',
    operatorBoundaryInternalWireguardTitle: 'Internal WireGuard',
    operatorBoundaryInternalWireguardCopy:
        'Plain WireGuard remains the intranet/control-plane path for operators and must not be presented as the user-facing regular VPN.',
    operatorBoundaryRegularVpnTitle: 'Regular VPN: AmneziaWG',
    operatorBoundaryRegularVpnCopy:
        'User-facing regular VPN belongs to AmneziaWG on pc_vpn hosts after backend/dev-tools live proof; this UI only labels the boundary.',
    operatorBoundaryVpnPlusTitle: 'VPN+: VLESS/H1Cloud',
    operatorBoundaryVpnPlusCopy: 'VPN+ remains a separate VLESS/H1Cloud product path and is not controlled by the internal WireGuard page.',
    operatorBoundaryMediaWorkerTitle: 'Media worker',
    operatorBoundaryMediaWorkerCopy:
        '/transcribe, /describe, and Suno media workloads are future pc_worker routing work; this page must not imply live migration controls exist.',
    operatorBoundaryNoLiveControlBadge: 'No live control',
    hostControlsEyebrow: 'Host controls',
    httpsNginxTitle: 'HTTPS + nginx operations',
    httpsNginxMeta: 'Refresh the live snapshot, validate nginx, reload nginx, renew certificates, or reload a specific Next.js lane.',
    dependencyEyebrowLocal: 'Local host',
    dependencyEyebrowWorkers: 'Backend workers',
    dependencyEyebrowRemote: 'Remote dependency',
    dependencySummaryHealthy: 'healthy',
    dependencySummaryAttention: 'needs attention',
    badgeLive: 'Live',
    badgeUnavailable: 'Unavailable',
    confirmationEyebrow: 'Guardrail',
    confirmationTitle: 'Live infrastructure actions require an explicit operator confirmation',
    confirmationCopy:
        'Reloads and certificate renewals touch the shared runtime immediately. Arm the action first, then type the exact confirmation phrase before it can run.',
    confirmationBadgeGuarded: 'Guarded',
    confirmationBadgeArmed: 'Armed',
    confirmationPendingAction: '{action} for {scope} is armed.',
    confirmationPhraseCopy: 'Type the exact phrase below to continue with the live action.',
    confirmationPhraseLabel: 'Exact confirmation phrase',
    confirmationCancel: 'Cancel',
    confirmationConfirm: 'Run live action',
    confirmationRunning: 'Running…',
    confirmationActionRenewCertificate: 'Renew certificate',
    confirmationActionReloadNginx: 'Reload nginx',
    confirmationActionReloadApp: 'Reload app',
    actionRefreshSnapshot: 'Refresh snapshot',
    actionRefreshingSnapshot: 'Refreshing…',
    actionTestNginx: 'Test nginx config',
    actionTestingNginx: 'Testing…',
    actionReloadNginx: 'Reload nginx',
    actionReloadingNginx: 'Reloading…',
    nginxEyebrow: 'nginx',
    nginxCardTitle: 'Web proxy health',
    labelServiceState: 'Service state',
    labelConfigTest: 'Config test',
    resultPassing: 'passing',
    resultFailing: 'failing',
    stateUnknown: 'unknown',
    certbotEyebrow: 'Certbot',
    certbotCardTitle: 'Renewal timer',
    labelNextRun: 'Next run',
    labelLastRun: 'Last run',
    valueUnknown: 'Unknown',
    sharedWith: 'Shared with',
    httpsBadgeOk: 'HTTPS OK',
    httpsBadgeAttention: 'Needs attention',
    labelHealthProbe: 'Health probe',
    labelCertificate: 'Certificate',
    labelPm2Lane: 'PM2 lane',
    labelUptime: 'Uptime',
    labelOnlineWorkers: 'Online workers',
    labelSummary: 'Summary',
    btnRenewCert: 'Renew cert',
    btnRenewSharedCert: 'Renew shared cert',
    btnRenewing: 'Renewing…',
    btnDryRunRenew: 'Dry-run renew',
    btnDryRunShared: 'Dry-run shared cert',
    btnDryRunTesting: 'Testing…',
    btnReloadApp: 'Reload app',
    btnReloadingApp: 'Reloading…',
    certsEyebrow: 'Certificates',
    certsLineagesTitle: 'Installed lineages',
    thName: 'Name',
    thDomains: 'Domains',
    thExpiry: 'Expiry',
    thStatus: 'Status',
    outputEyebrow: 'Latest action output',
    outputTitle: 'Command log',
    errRefreshFailed: 'Failed to refresh infrastructure state.',
    errActionFailed: 'Infrastructure action failed.',
    actionSuccessLine: '{action} completed successfully.',
    missingCertificate: 'Missing',
    certExpired: 'Expired',
    certInvalid: 'Invalid',
    certValid: 'Valid',
    certDaysLeft: '{days} days left',
    durationUnknown: 'Unknown',
    durationDaysHours: '{days}d {hours}h',
    durationHoursMinutes: '{hours}h {minutes}m',
    durationMinutes: '{minutes}m',
    backendWorkersEyebrow: 'Workers',
    backendWorkersTitle: 'Backend runtime workers',
    backendWorkersSummary: '{online}/{total} online',
    backendWorkersEmpty: 'No backend worker processes were detected on this host.',
    remoteHostsEyebrow: 'Remote host',
    remoteHostReachable: 'reachable',
    remoteHostUnreachable: 'unreachable',
    envPreview: 'preview',
    envPreview2: 'preview-2',
    envDevelopment: 'development',
    envDevelopment2: 'development-2',
    envProduction: 'production',
    totalCertificatesBadge: '{count} total',
} satisfies Dictionary['admin']['infrastructure'];
